import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { contextAggregation } from '@shared/services/contextAggregation';
import type { UserCareerContext, CoachingAlert, CoachingOpportunity } from '@shared/types/ai-coach-context.types';

interface AICoachContextType {
  context: UserCareerContext | null;
  loading: boolean;
  error: string | null;
  refreshContext: () => Promise<void>;
  urgentAlerts: CoachingAlert[];
  opportunities: CoachingOpportunity[];
  motivationLevel: 'low' | 'medium' | 'high';
  hasStagnation: boolean;
  nextBestActions: Array<{
    action: string;
    rationale: string;
    effort: 'low' | 'medium' | 'high';
    impact: 'low' | 'medium' | 'high';
  }>;
}

const AICoachContext = createContext<AICoachContextType | undefined>(undefined);

export const useAICoach = () => {
  const context = useContext(AICoachContext);
  if (!context) {
    throw new Error('useAICoach must be used within AICoachProvider');
  }
  return context;
};

interface AICoachProviderProps {
  children: React.ReactNode;
}

export const AICoachProvider: React.FC<AICoachProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [context, setContext] = useState<UserCareerContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContext = useCallback(async () => {
    if (!user) {
      setContext(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const careerContext = await contextAggregation.getUserContext(user.id, {
        includeReport: true,
        includeSkills: true,
        includeBRAG: true,
        includeDREAM: true,
        includeBehavior: true
      });

      setContext(careerContext);
      setError(null);
    } catch (err: any) {
      console.error('Error loading AI Coach context:', err);

      // Set error but don't block - allow degraded mode
      const errorMsg = err.message || 'Failed to load coaching context';
      setError(errorMsg);

      // Provide minimal context so features still work
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refreshContext = useCallback(async () => {
    if (!user) return;

    try {
      const careerContext = await contextAggregation.getUserContext(user.id, {
        includeReport: true,
        includeSkills: true,
        includeBRAG: true,
        includeDREAM: true,
        includeBehavior: true,
        forceRefresh: true
      });

      setContext(careerContext);
    } catch (err: any) {
      console.error('Error refreshing context:', err);
    }
  }, [user]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const urgentAlerts = context?.insights.urgentItems || [];
  const opportunities = context?.insights.opportunities || [];
  const motivationLevel = context?.insights.motivationLevel || 'medium';
  const hasStagnation = context?.skills.stagnationFlags.hasStagnantSkills || false;
  const nextBestActions = context?.insights.nextBestActions || [];

  const value: AICoachContextType = {
    context,
    loading,
    error,
    refreshContext,
    urgentAlerts,
    opportunities,
    motivationLevel,
    hasStagnation,
    nextBestActions
  };

  return <AICoachContext.Provider value={value}>{children}</AICoachContext.Provider>;
};
